import { useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/misc/Breadcrumbs'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Search, Plus, MoreHorizontal, Edit, Trash2 } from 'lucide-react'
import { getProducts } from '@/services/mock/products'

export default function CategoriesPage() {
  const [products, setProducts] = useState([])
  const [page, setPage] = useState(1)
  const [size] = useState(10)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [tab, setTab] = useState('ALL')
  const [loading, setLoading] = useState(false)

  const fetchProducts = async () => {
    setLoading(true)
    try {
      const res = await getProducts({ page, size, search })
      setProducts(res.data)
      setTotal(res.meta.total)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProducts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, tab])

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Categories</h1>
          <Breadcrumbs />
        </div>
      </div>

      {/* Discover categories */}
      <section className="space-y-4">
        <h2 className="text-lg font-medium">Discover</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-4">
          {[
            'Hoodie',
            'Fashion',
            'Accessories',
            'Home & Kitchen',
            'Sports & Outdoors',
            'Toys & Games',
            'Health & Fitness',
            'Books',
          ].map((cat) => (
            <div key={cat} className="flex items-center gap-3 p-3 border rounded bg-card">
              <div className="h-10 w-10 rounded bg-muted flex items-center justify-center">📦</div>
              <div className="font-medium">{cat}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Controls and product list */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="primary" size="md" className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Product
            </Button>
            <Button variant="ghost" size="md" className="flex items-center gap-2">
              More Action
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center gap-2 w-full max-w-md">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} className="pl-10" placeholder="Search your product" />
            </div>
          </div>
        </div>

        <div className="bg-card border p-4 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <button onClick={() => setTab('ALL')} className="px-3 py-1 rounded-full bg-primary text-primary-foreground text-sm">All Product ({total || 0})</button>
              <button onClick={() => setTab('FEATURED')} className="px-3 py-1 rounded-full text-sm bg-muted/50">Featured Products</button>
              <button onClick={() => setTab('ONSALE')} className="px-3 py-1 rounded-full text-sm bg-muted/50">On Sale</button>
              <button onClick={() => setTab('OUT')} className="px-3 py-1 rounded-full text-sm bg-muted/50">Out of Stock</button>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm">Filters</Button>
              <Button variant="ghost" size="sm">More</Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="p-3">No.</th>
                  <th className="p-3">Product</th>
                  <th className="p-3">Created Date</th>
                  <th className="p-3">Order</th>
                  <th className="p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">Loading...</td></tr>
                ) : products.length === 0 ? (
                  <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">No products found</td></tr>
                ) : (
                  products.map((p, idx) => (
                    <tr key={p.id} className="border-b last:border-b-0 hover:bg-muted/50">
                      <td className="p-3 align-middle">{(page - 1) * size + idx + 1}</td>
                      <td className="p-3 align-middle flex items-center gap-3">
                        <div className="h-8 w-8 rounded bg-muted flex items-center justify-center">🖼️</div>
                        <div>
                          <div className="font-medium">{p.name}</div>
                          <div className="text-muted-foreground text-xs">{p.description}</div>
                        </div>
                      </td>
                      <td className="p-3 align-middle">{new Date(p.createdAt).toLocaleDateString()}</td>
                      <td className="p-3 align-middle">{p.order || '-'}</td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm"><Edit className="h-4 w-4" /></Button>
                          <Button variant="destructive" size="sm"><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">Showing {(page - 1) * size + 1} - {Math.min(page * size, total)} of {total}</div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</Button>
              <div className="px-3 py-1 rounded bg-muted text-sm">{page}</div>
              <Button variant="ghost" size="sm" disabled={page * size >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}